import { Icon, Stack, Tooltip, Typography } from '@mui/material';
import {
  Assessment,
  AssessmentBase,
  DATE_FORMAT_DAY,
  DATE_FORMAT_MONTH_YEAR,
  SHORT_FORMATTED_DATE_FORMAT_FNS,
} from '@schooly/api';
import { AssessmentStatuses } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  ReportGeneratedIcon,
  ReportsIcon,
  SquareCard,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Link, LinkProps } from 'react-router-dom';

import useAppLocation from '../../../../hooks/useAppLocation';
import { AssessmentReportsTooltip } from '../../AssessmentReportsTooltip';

interface MarkbookAssessmentInfoProps<T> {
  assessment: T;
  linkProps?: Omit<LinkProps, 'to'>;
  assessmentDateVisible?: boolean;
}

export const MarkbookAssessmentInfo: FC<
  MarkbookAssessmentInfoProps<AssessmentBase & Pick<Assessment, 'reports'>>
> = ({ assessment, linkProps, assessmentDateVisible = true }) => {
  const location = useAppLocation();
  const { formatMessage } = useIntl();
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const assessmentDate = newDateTimezoneOffset(assessment.assessment_date);
  const assessmentDay = format(assessmentDate, DATE_FORMAT_DAY);
  const assessmentMonthYear = format(assessmentDate, DATE_FORMAT_MONTH_YEAR);

  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  const autoGeneratedFromReport = assessment.autogenerated;
  const hasReports = !!assessment.reports.length;
  const showReportIcon = autoGeneratedFromReport || hasReports;

  const isPublished = assessment.status === AssessmentStatuses.Published;

  return (
    <Stack gap={1} sx={({ spacing }) => ({ py: 1, minHeight: spacing(7.75) })} direction="row">
      {assessmentDateVisible && (
        <Tooltip
          componentsProps={{ tooltip: { sx: { p: 1.25, width: 200 } } }}
          title={
            isPublished ? (
              <>
                {formatMessage({ id: 'status-Published' })}{' '}
                {assessment.publish_date
                  ? format(
                      newDateTimezoneOffset(assessment.publish_date),
                      SHORT_FORMATTED_DATE_FORMAT_FNS,
                    )
                  : ''}
              </>
            ) : (
              formatMessage({ id: 'status-NotPublished' })
            )
          }
        >
          <SquareCard
            sx={{
              cursor: 'pointer',
              borderColor: isPublished ? 'success.light' : 'common.orange4',
              backgroundColor: isPublished ? 'success.superLight' : 'common.orange5',
              '.MuiTypography-root': {
                color: isPublished ? 'success.main' : 'warning.main',
              },
              '&:hover': {
                borderColor: isPublished ? 'success.main' : 'warning.main',
                backgroundColor: isPublished ? 'success.main' : 'warning.main',
                '.MuiTypography-root': {
                  color: isPublished ? 'success.superLight' : 'common.orange5',
                },
              },
            }}
          >
            <Typography mt={0.25} variant="h2">
              {assessmentDay}
            </Typography>
            <Typography variant="caption">{assessmentMonthYear}</Typography>
          </SquareCard>
        </Tooltip>
      )}

      <Stack overflow="hidden" justifyContent="center">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography
            variant="body1"
            color="text.primary"
            component={Link}
            to={{ pathname: `/assessments/${assessment.id}` }}
            state={
              location.state?.backgroundLocation ? location.state : { backgroundLocation: location }
            }
            sx={{
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
              display: '-webkit-box',
              WebkitLineClamp: evaluationTitle ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
            }}
            {...linkProps}
          >
            {assessment.name}
          </Typography>

          {showReportIcon && (
            <AssessmentReportsTooltip assessment={assessment}>
              <Icon>{autoGeneratedFromReport ? <ReportGeneratedIcon /> : <ReportsIcon />}</Icon>
            </AssessmentReportsTooltip>
          )}
        </Stack>

        {evaluationTitle && (
          <TypographyWithOverflowHint color="text.primary">
            {evaluationTitle}
          </TypographyWithOverflowHint>
        )}
      </Stack>
    </Stack>
  );
};
